import * as React from 'react';
import {useFormContext} from 'react-hook-form';

import {InputStyled, SelectStyled} from '../../elements/forms/InputContainer';
import {SelectContainer} from './AccountContainer.style';
import {QueryUsers, UserOption} from '../../../services/alderaan/QueryUsers';

const SEARCH_DEBOUNCE_MS = 400;

type OwnerMode = 'existing' | 'new';

function ownerLabel(user: UserOption): string {
  const fullName = [user.name, user.lastName].filter(Boolean).join(' ');
  return fullName ? `${fullName} (${user.email})` : user.email;
}

export const AccountOwnerComponent = (): JSX.Element => {
  const {setValue, getValues, watch} = useFormContext();
  const owner = watch('owner');

  const [mode, setMode] = React.useState<OwnerMode>(
    owner?.email && !owner?.id ? 'new' : 'existing',
  );
  const [options, setOptions] = React.useState<UserOption[]>([]);
  const [searching, setSearching] = React.useState<boolean>(false);
  const [term, setTerm] = React.useState<string>('');

  React.useEffect(() => {
    if (mode !== 'existing' || term.length < 2) {
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      QueryUsers(term).then((users) => {
        setOptions(users);
        setSearching(false);
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [term, mode]);

  const currentOwner = getValues('owner');

  const selectedOption = currentOwner?.id
    ? {
        value: currentOwner.id,
        label: ownerLabel(currentOwner as UserOption),
      }
    : null;

  function changeMode(nextMode: OwnerMode) {
    setMode(nextMode);
    setValue('owner', undefined);
  }

  function selectExisting(option: any) {
    const user = options.find((item) => String(item.id) === String(option?.value));
    setValue('owner', user ? {id: user.id, name: user.name, lastName: user.lastName, email: user.email} : undefined);
  }

  function updateNewOwner(field: string, value: string) {
    const previous = getValues('owner') || {};
    setValue('owner', {...previous, id: undefined, [field]: value});
  }

  return (
    <div className="row">
      <div className="col s12">
        <h3>Responsável pela conta</h3>
        <p>
          Esse usuário será o dono da conta e receberá o e-mail de boas-vindas.
          Sem essa escolha, a conta fica no seu próprio usuário.
        </p>
      </div>

      <div className="col s12">
        <label>
          <input
            type="radio"
            checked={mode === 'existing'}
            onChange={() => changeMode('existing')}
          />
          <span>Usuário já cadastrado</span>
        </label>
        <label style={{marginLeft: '1.5rem'}}>
          <input
            type="radio"
            checked={mode === 'new'}
            onChange={() => changeMode('new')}
          />
          <span>Criar novo usuário</span>
        </label>
      </div>

      {mode === 'existing' && (
        <SelectContainer className="col s12">
          <SelectStyled
            label="Buscar usuário"
            isClearable
            value={selectedOption}
            isLoading={searching}
            onInputChange={(value: string) => setTerm(value)}
            onChange={selectExisting}
            options={options.map((user) => ({
              value: user.id,
              label: ownerLabel(user),
            }))}
            placeholder="Digite nome ou e-mail"
            noOptionsMessage={() =>
              term.length < 2
                ? 'Digite ao menos 2 caracteres'
                : 'Nenhum usuário encontrado'
            }
          />
        </SelectContainer>
      )}

      {mode === 'new' && (
        <>
          <div className="col s12 m4">
            <InputStyled
              label="Nome"
              value={currentOwner?.name || ''}
              onChange={(event: any) => updateNewOwner('name', event.target.value)}
            />
          </div>
          <div className="col s12 m4">
            <InputStyled
              label="Sobrenome"
              value={currentOwner?.lastName || ''}
              onChange={(event: any) => updateNewOwner('lastName', event.target.value)}
            />
          </div>
          <div className="col s12 m4">
            <InputStyled
              label="E-mail"
              value={currentOwner?.email || ''}
              onChange={(event: any) => updateNewOwner('email', event.target.value)}
            />
          </div>
        </>
      )}
    </div>
  );
};
